import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { FaChevronRight } from "react-icons/fa";
import { ACTIVITY_API_BASE } from "../lib/activityApi";
import CitySelector from "../components/activities/CitySelector";
import ActivityGrid from "../components/activities/ActivityGrid";

const baseUrl = ACTIVITY_API_BASE.replace("/api/activity", "");

export default function CityActivitiesPage() {
  const { countryId, cityId } = useParams();
  const navigate = useNavigate();
  const [country, setCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [activities, setActivities] = useState([]);
  const [minPriceMap, setMinPriceMap] = useState({});
  const [selectedCityId, setSelectedCityId] = useState(cityId || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!countryId) return;
    axios.get(`${ACTIVITY_API_BASE}/user/countries`).then((res) => {
      const list = res.data?.data || [];
      const c = list.find((x) => String(x._id) === String(countryId));
      setCountry(c || null);
    });
    axios.get(`${ACTIVITY_API_BASE}/user/cities/${countryId}`).then((res) => {
      const list = res.data?.data || [];
      setCities(list);
      if (cityId) setSelectedCityId(cityId);
      else setSelectedCityId(null);
    }).catch(() => setCities([])).finally(() => setLoading(false));
  }, [countryId, cityId]);

  useEffect(() => {
    if (!selectedCityId) {
      setActivities([]);
      setMinPriceMap({});
      return;
    }
    setLoading(true);
    axios.get(`${ACTIVITY_API_BASE}/user/activities/${selectedCityId}`).then((res) => {
      const list = res.data?.data || [];
      setActivities(list);
      const ids = list.map((a) => a._id).slice(0, 24);
      const map = {};
      Promise.all(
        ids.map((id) =>
          axios.get(`${ACTIVITY_API_BASE}/user/activity-options/${id}`).then((r) => {
            const opts = r.data?.data || [];
            const prices = opts.map((o) => Number(o.basePrice) || 0).filter((p) => p > 0);
            if (prices.length) map[id] = Math.min(...prices);
          }).catch(() => {})
        )
      ).then(() => setMinPriceMap(map));
    }).catch(() => setActivities([])).finally(() => setLoading(false));
  }, [selectedCityId]);

  const selectedCity = cities.find((c) => c._id === selectedCityId);

  const handleActivityClick = (activity) => {
    navigate(`/activity/${activity._id}`, {
      state: { countryId, cityId: selectedCityId, country, city: selectedCity },
    });
  };

  if (!countryId) {
    navigate("/activities", { replace: true });
    return null;
  }

  if (loading && !country) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1ABCE3]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <button type="button" onClick={() => navigate("/activities")} className="hover:text-[#1ABCE3]">
            Activities
          </button>
          <FaChevronRight className="w-3 h-3" />
          <span className="text-[#222222] font-medium">{country?.name}</span>
          {selectedCity && (
            <>
              <FaChevronRight className="w-3 h-3" />
              <span className="text-[#222222] font-medium">{selectedCity.name}</span>
            </>
          )}
        </div>

        <CitySelector
          cities={cities}
          selectedCityId={selectedCityId}
          onSelect={(city) => navigate(`/activities/${countryId}/${city._id}`)}
          baseUrl={baseUrl}
          countryName={country?.name}
        />

        {selectedCityId && selectedCity && (
          <>
            <div className="mb-4">
              <h2 className="text-xl md:text-2xl font-bold text-[#222222]">
                Top Attractions in {selectedCity.name}
              </h2>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1ABCE3]" />
              </div>
            ) : (
              <ActivityGrid
                activities={activities}
                minPriceMap={minPriceMap}
                baseUrl={baseUrl}
                onActivityClick={handleActivityClick}
              />
            )}
          </>
        )}

        {!selectedCityId && cities.length > 0 && (
          <p className="text-center text-gray-500 py-8">
            Select a city above to see activities and check availability.
          </p>
        )}
      </div>

      {/* Floating WhatsApp */}
      <a
        href="https://wa.me/"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform"
        style={{ backgroundColor: "#25D366" }}
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>
    </div>
  );
}
